import React, { useEffect, useState, useCallback } from "react";
import { Button, Dropdown, Input, Pagination, Select, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faFolderTree, faSync, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import postApi from "../../../api/postApi";
import useIsMobile from "../../../utils/use-is-mobile";
import VideoTable from "./components/video-table";
import VideoCardList from "./components/video-card-list";
import VideoProductFormModal from "./components/video-product-form-modal";
import CategoryManagerModal from "./components/category-manager-modal";
import BulkActionBar from "./components/bulk-action-bar";

let sendTimer;

function VideoList() {
    const [videos, setVideos] = useState([]);
    const [totalVideos, setTotalVideos] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [categoryModalOpen, setCategoryModalOpen] = useState(false);
    const [recheckLoading, setRecheckLoading] = useState(false);
    const [filter, setFilter] = useState({ limit: 20, page: 1, productName: "", categoryId: undefined });
    const isMobile = useIsMobile();

    const fetchCategories = useCallback(async () => {
        try {
            const { data } = await postApi.getVideoCategories();
            setCategories(data.videoCategories || []);
        } catch (e) {
            console.error("Failed to fetch categories:", e);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        const fetchVideos = async () => {
            setIsLoading(true);
            const qp = [];
            if (filter.productName) qp.push(`productName=${encodeURIComponent(filter.productName)}`);
            if (filter.categoryId) qp.push(`categoryId=${encodeURIComponent(filter.categoryId)}`);
            // Mới nhất lên đầu (id giảm dần) — chỉ áp dụng cho admin.
            qp.push(`page=${filter.page}`, `limit=${filter.limit}`, `withProducts=1`, `sort=-id`);
            try {
                const { data } = await postApi.getVideoList(qp.join("&"));
                setVideos(data.videos || []);
                setTotalVideos(data.totalVideos || 0);
            } catch (e) {
                message.error("Không thể tải danh sách video");
            } finally {
                setIsLoading(false);
            }
        };
        if (sendTimer) clearTimeout(sendTimer);
        sendTimer = setTimeout(fetchVideos, filter.productName ? 800 : 0);
    }, [filter]);

    const reload = () => setFilter((p) => ({ ...p }));
    const setF = (key, value) => setFilter((p) => ({ ...p, [key]: value, page: 1 }));

    const handleEdit = (video) => {
        setEditingVideo(video);
        setFormOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await postApi.deleteVideoList(id);
            message.success("Đã xoá video");
            reload();
        } catch (e) {
            message.error("Không thể xoá video");
        }
    };

    // Kiểm tra 1 video: cả link video + sản phẩm gắn kèm.
    const handleRecheck = async (id) => {
        try {
            const [link, prod] = await Promise.all([
                postApi.recheckVideoLinks([id]),
                postApi.recheckVideoProducts([id]),
            ]);
            const v = link.data.summary || {};
            const p = prod.data.summary || {};
            message.success(
                `Video: ${v.dead ? "đã gỡ" : v.alive ? "còn sống" : "?"} · SP: ${p.total || 0} (lỗi ${p.error || 0})`
            );
            reload();
        } catch (e) {
            message.error("Không thể kiểm tra video");
        }
    };

    const runBulk = async (fn, successMsg) => {
        if (!selectedRowKeys.length) return;
        setRecheckLoading(true);
        try {
            await fn(selectedRowKeys);
            message.success(successMsg);
            setSelectedRowKeys([]);
            reload();
        } catch (e) {
            message.error("Thao tác thất bại");
        } finally {
            setRecheckLoading(false);
        }
    };

    const handleCheckAllVideos = async () => {
        setRecheckLoading(true);
        try {
            const { data } = await postApi.recheckAllVideoLinks();
            const s = data.summary || {};
            message.success(`Video: xử lý ${s.processed || 0} — chết: ${s.dead || 0}, còn lại: ${data.remaining || 0}`);
            reload();
        } catch (e) {
            message.error("Không thể kiểm tra video");
        } finally {
            setRecheckLoading(false);
        }
    };

    const handleCheckAllProducts = async () => {
        setRecheckLoading(true);
        try {
            const { data } = await postApi.recheckAllVideoProducts();
            const s = data.summary || {};
            message.success(`Sản phẩm: xử lý ${s.processed || 0} — lỗi: ${s.error || 0}, còn lại: ${data.remaining || 0}`);
            reload();
        } catch (e) {
            message.error("Không thể kiểm tra sản phẩm");
        } finally {
            setRecheckLoading(false);
        }
    };

    const categoryOptions = [
        { label: "Tất cả", value: undefined },
        ...categories.map((c) => ({ label: c.categoryName || c.categoryId, value: c.categoryId })),
    ];

    // Menu gom các thao tác phụ trên mobile để header không tràn màn hình.
    const moreMenuItems = [
        { key: "checkVideos", icon: <FontAwesomeIcon icon={faSync} />, label: "Kiểm tra tất cả video", onClick: handleCheckAllVideos },
        { key: "checkProducts", icon: <FontAwesomeIcon icon={faSync} />, label: "Kiểm tra tất cả sản phẩm", onClick: handleCheckAllProducts },
        { key: "categories", icon: <FontAwesomeIcon icon={faFolderTree} />, label: "Danh mục", onClick: () => setCategoryModalOpen(true) },
    ];

    return (
        <div className="p-3 sm:p-4 w-full h-full flex flex-col gap-2 overflow-auto">
            <div className="flex flex-wrap gap-2 justify-between items-center">
                <p className="p-1 sm:p-2 text-xl font-bold">Quản lý Video</p>
                <div className="flex gap-2">
                    {isMobile ? (
                        <Dropdown menu={{ items: moreMenuItems }} trigger={["click"]}>
                            <Button loading={recheckLoading} icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
                        </Dropdown>
                    ) : (
                        <>
                            <Button icon={<FontAwesomeIcon icon={faSync} />} loading={recheckLoading} onClick={handleCheckAllVideos}>
                                Kiểm tra tất cả video
                            </Button>
                            <Button icon={<FontAwesomeIcon icon={faSync} />} loading={recheckLoading} onClick={handleCheckAllProducts}>
                                Kiểm tra tất cả sản phẩm
                            </Button>
                            <Button icon={<FontAwesomeIcon icon={faFolderTree} />} onClick={() => setCategoryModalOpen(true)}>
                                Danh mục
                            </Button>
                        </>
                    )}
                    <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => { setEditingVideo(null); setFormOpen(true); }}>
                        Thêm Video
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <Input
                    value={filter.productName}
                    onChange={(e) => setF("productName", e.target.value)}
                    placeholder="Tìm theo tên"
                    allowClear
                    className="w-full sm:w-60"
                />
                <Select
                    value={filter.categoryId}
                    onChange={(v) => setF("categoryId", v)}
                    allowClear
                    options={categoryOptions}
                    placeholder="Danh mục"
                    className="w-full sm:w-48"
                />
            </div>

            <BulkActionBar
                selectedCount={selectedRowKeys.length}
                categories={categories}
                loading={recheckLoading}
                onBulkDelete={() => runBulk((ids) => postApi.deleteVideoListBulk(ids), "Đã xoá")}
                onBulkMove={(catId) => runBulk((ids) => postApi.updateVideoListBulk(ids, catId), "Đã chuyển danh mục")}
                onBulkRecheck={() => runBulk(
                    (ids) => Promise.all([postApi.recheckVideoLinks(ids), postApi.recheckVideoProducts(ids)]),
                    "Đã kiểm tra video + sản phẩm"
                )}
            />

            <div className="overflow-auto flex-1">
                {isMobile ? (
                    <VideoCardList
                        videos={videos}
                        loading={isLoading}
                        selectedRowKeys={selectedRowKeys}
                        onSelectChange={setSelectedRowKeys}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onRecheck={handleRecheck}
                    />
                ) : (
                    <VideoTable
                        videos={videos}
                        loading={isLoading}
                        selectedRowKeys={selectedRowKeys}
                        onSelectChange={setSelectedRowKeys}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onRecheck={handleRecheck}
                    />
                )}
            </div>

            <div className={isMobile ? "flex justify-center pb-2" : "flex justify-end"}>
                <Pagination
                    total={totalVideos}
                    current={filter.page}
                    pageSize={filter.limit}
                    simple={isMobile}
                    showSizeChanger={!isMobile}
                    pageSizeOptions={[20, 50, 100, 200]}
                    onChange={(page, limit) => setFilter((p) => ({ ...p, page, limit }))}
                />
            </div>

            <VideoProductFormModal
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditingVideo(null); }}
                onSaved={reload}
                categories={categories}
                editingVideo={editingVideo}
            />
            <CategoryManagerModal
                open={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                categories={categories}
                onChanged={fetchCategories}
            />
        </div>
    );
}

export default VideoList;
